// Dispatch is calculated in the shop's timezone, independently of the visitor.
const dayMs = 86400000;
const holidayCache = new Map();
let shopClock;
let dispatchDateFormat;
const dateKey = date => date.toISOString().slice(0, 10);
const utcDate = (year, month, day) => new Date(Date.UTC(year, month - 1, day));

function firstMonday(year, month) {
  const date = utcDate(year, month, 1);
  date.setUTCDate(1 + (8 - date.getUTCDay()) % 7);
  return date;
}

function easterSunday(year) {
  // Gregorian computus; the dispatch closure is the following Monday.
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const value = h + l - 7 * m + 114;
  return utcDate(year, Math.floor(value / 31), value % 31 + 1);
}

export function irishPublicHolidays(year) {
  if (holidayCache.has(year)) return holidayCache.get(year);
  const easterMonday = new Date(easterSunday(year).getTime() + dayMs);
  const october = utcDate(year, 10, 31);
  october.setUTCDate(31 - (october.getUTCDay() + 6) % 7);
  const holidays = [
    utcDate(year, 1, 1), utcDate(year, 3, 17), easterMonday,
    firstMonday(year, 5), firstMonday(year, 6), firstMonday(year, 8),
    october, utcDate(year, 12, 25), utcDate(year, 12, 26)
  ];
  if (year >= 2023) {
    const february = utcDate(year, 2, 1);
    holidays.push(february.getUTCDay() === 5 ? february : firstMonday(year, 2));
  }
  // Weekend holidays do not automatically imply a Monday business closure.
  // Courier substitute days and shop closures belong in the editable date list.
  const keys = new Set(holidays.map(dateKey));
  holidayCache.set(year, keys);
  return keys;
}

export function parseCutoff(value) {
  const match = String(value).trim().toLowerCase().match(/^(\d{1,2})(?::([0-5]\d))?\s*(am|pm)?$/);
  if (!match) return null;
  let hour = Number(match[1]);
  if (match[3]) {
    if (hour < 1 || hour > 12) return null;
    hour = hour % 12 + (match[3] === 'pm' ? 12 : 0);
  } else if (hour > 23) return null;
  return hour * 3600 + Number(match[2] || 0) * 60;
}

export function dispatchMessage(now, cutoff, closureText = '') {
  const cutoffSeconds = parseCutoff(cutoff);
  if (cutoffSeconds === null || !Number.isFinite(now.getTime())) return null;
  const extraClosures = new Set(String(closureText).trim().split(/[\s,;]+/).filter(Boolean));
  for (const key of extraClosures) {
    const parsed = new Date(`${key}T00:00:00Z`);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(key) || !Number.isFinite(parsed.getTime()) || dateKey(parsed) !== key) return null;
  }
  shopClock ||= new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Dublin', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
  });
  const parts = Object.fromEntries(shopClock.formatToParts(now).map(part => [part.type, part.value]));
  const today = utcDate(Number(parts.year), Number(parts.month), Number(parts.day));
  const seconds = Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second);
  const isDispatchDay = date => date.getUTCDay() !== 0 && date.getUTCDay() !== 6
    && !irishPublicHolidays(date.getUTCFullYear()).has(dateKey(date)) && !extraClosures.has(dateKey(date));

  if (isDispatchDay(today) && seconds < cutoffSeconds) {
    const remaining = cutoffSeconds - seconds;
    const minutes = Math.floor(remaining / 60);
    const hours = Math.floor(minutes / 60);
    const time = remaining < 60 ? 'less than 1 minute' : `${hours ? `${hours}h ` : ''}${minutes % 60}m`;
    return `Order within ${time} for dispatch today`;
  }
  for (let days = 1; days <= 366; days += 1) {
    const next = new Date(today.getTime() + days * dayMs);
    if (isDispatchDay(next)) {
      if (days === 1) return 'Order now for dispatch tomorrow';
      dispatchDateFormat ||= new Intl.DateTimeFormat('en-IE', {
        timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'short'
      });
      const label = dispatchDateFormat.format(next);
      return `Order now for dispatch ${label}`;
    }
  }
  return null;
}

function initDispatchPanels() {
  let timer;
  const refresh = () => {
    const panels = document.querySelectorAll('[data-leavys-delivery]');
    if (!panels.length && timer) {
      clearInterval(timer);
      timer = undefined;
    }
    panels.forEach(panel => {
      const message = panel.querySelector('[data-dispatch-message]');
      if (!message) return;
      const fallback = 'Dispatch Monday–Friday, excluding Irish public holidays.';
      let text = fallback;
      if (panel.dataset.dispatchEnabled !== 'false') {
        const form = panel.closest('.product-block--form')?.querySelector('[data-product-form]');
        const variantId = form?.querySelector('[data-variants]')?.value || panel.dataset.selectedVariant;
        const eligibleIds = panel.dataset.dispatchVariantIds.split(',');
        const unavailable = form?.querySelector('[name="add"]')?.disabled;
        if (!eligibleIds.includes(variantId) || unavailable) {
          text = 'Choose an in-stock size for dispatch information.';
        } else {
          try {
            text = dispatchMessage(new Date(), panel.dataset.dispatchCutoff, panel.dataset.dispatchClosures) || fallback;
          } catch {
            // Keep the useful static message if timezone support is unavailable.
          }
        }
      }
      // No live region: minute updates must not repeatedly interrupt screen readers.
      if (message.textContent !== text) message.textContent = text;
    });
  };
  const start = () => {
    refresh();
    if (!timer && document.querySelector('[data-leavys-delivery]')) {
      // Check the deadline each second; only write when the minute/message changes.
      timer = setInterval(() => { if (!document.hidden) refresh(); }, 1000);
    }
  };
  start();
  // Turbo dispatches a non-bubbling change on its variant select; capture it.
  document.addEventListener('change', event => {
    if (event.target.matches('[data-variants]')) requestAnimationFrame(refresh);
  }, true);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); });
  window.addEventListener('pageshow', refresh);
  document.addEventListener('shopify:section:load', start);
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(refresh));
}

if (typeof document !== 'undefined') initDispatchPanels();
